import { Fragment, memo, useState } from 'react';
import { cardSrc, parseCards } from './cards/cardUtils';
import { roundBook, fmtBook } from '../../utils/casinoBook';

function isSusp(item) {
  return !item || item.gstatus === 'SUSPENDED' || item.gstatus === '0';
}

function suspCls(item) {
  return isSusp(item) ? ' suspended' : '';
}

function fmtAmt(n) {
  const x = Number(n) || 0;
  if (x >= 100000) return `${x / 100000}L`;
  if (x >= 1000) return `${x / 1000}K`;
  return String(x);
}

function bookOf(exposures, name, sid) {
  return roundBook(exposures?.[`sid:${sid}`] ?? exposures?.[name] ?? 0);
}

function bookCls(v) {
  return v < 0 ? 'book-red' : v > 0 ? 'book-green' : 'book-black';
}

const PLAYERS = [1, 2, 3, 4, 5, 6, 7, 8];

// 27 cards, 9-way round-robin (8 players + dealer):
//   P(n): codes[n-1], codes[9+n-1], codes[18+n-1]
function getPlayerCards(codes, n) {
  const i = n - 1;
  return [codes[i] || '1', codes[9 + i] || '1', codes[18 + i] || '1'];
}

// nation-name range collapse — i.float-right + collapse div (reference)
function RangeCollapse({ rangeId, min, max }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <i
        data-toggle="collapse"
        data-target={`#${rangeId}`}
        aria-expanded={open ? 'true' : 'false'}
        className={`fas fa-info-circle float-right${open ? '' : ' collapsed'}`}
        onClick={() => setOpen((o) => !o)}
      ></i>
      {' '}
      <div id={rangeId} className={`collapse icon-range${open ? ' show' : ''}`}>
        {'\n                      R:'}
        <span>{fmtAmt(min ?? 100)}</span>
        -
        <span>{fmtAmt(max ?? 0)}</span>
      </div>
    </>
  );
}

const BetTableTeenpattiOpen = memo(function BetTableTeenpattiOpen({ gameData, onBet, exposures }) {
  const sub = gameData?.sub || [];
  const codes = parseCards(gameData?.card || '');
  const handleBet = onBet || (() => {});

  const findItem = (nat) => sub.find((s) => s.nat === nat);

  // desktop back cell: odds + casino-book book-<clr>; pairPlus => open-pair label
  const backCell = (item, pairN, key) => {
    const book = bookOf(exposures, item?.nat, item?.sid);
    return (
      <div
        key={key}
        className={`back casino-bl-box-item${suspCls(item)}`}
        onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'back', item.b, item)}
      >
        {pairN
          ? <span className="casino-box-odd open-pair">Pair Plus {pairN}</span>
          : <span className="casino-box-odd">{item?.b ?? 0}</span>}
        {' '}
        <span className={`casino-book ${bookCls(book)}`}>{fmtBook(book)}</span>
      </div>
    );
  };

  // mobile back cell: book-<clr> (no casino-book), pairPlus label without open-pair
  const mBackCell = (item, pairN, key) => {
    const book = bookOf(exposures, item?.nat, item?.sid);
    return (
      <div
        key={key}
        className={`back casino-bl-box-item${suspCls(item)}`}
        onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'back', item.b, item)}
      >
        {pairN
          ? <span className="casino-box-odd">Pair Plus {pairN}</span>
          : <span className="casino-box-odd">{item?.b ?? 0}</span>}
        {' '}
        <span className={bookCls(book)}>{fmtBook(book)}</span>
      </div>
    );
  };

  const oddsRef = findItem('Player 1');
  const pairRef = findItem('Pair Plus 1');
  const totalRef = findItem('Total 1');

  return (
    <>
      {/* ── DESKTOP (d-none-small) ── */}
      <div className="casino-box mb-2 d-none-small">
        <div className="casino-open-card-box">
          {PLAYERS.map((n) => (
            <div key={n}>
              <div><b>{n}</b></div>
              {' '}
              <div>
                {/* reference: a space between the card spans */}
                {getPlayerCards(codes, n).map((c, i) => (
                  <Fragment key={i}>
                    {i > 0 && ' '}
                    <span data-v-b64efdfa="" showclosecard="true">
                      <img data-v-b64efdfa="" src={cardSrc(c)} />
                    </span>
                  </Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="casino-box-header">
          <div className="casino-nation-name no-border"></div>
          <div className="casino-bl-box text-right">
            {PLAYERS.map((n) => (
              <div key={n} className="casino-bl-box-item"><span>Player {n}</span></div>
            ))}
          </div>
        </div>

        <div className="casino-box-content">
          <div className="casino-box-row mb-4">
            <div className="casino-nation-name">
              <b>Odds</b>
              {' '}
              <RangeCollapse rangeId="range1" min={oddsRef?.min} max={oddsRef?.max ?? 100000} />
            </div>
            <div className="casino-bl-box">
              {PLAYERS.map((n) => backCell(findItem(`Player ${n}`), null, n))}
            </div>
          </div>

          <div className="casino-box-row mb-4">
            <div className="casino-nation-name">
              <b>Pair Plus</b>
              {' '}
              <RangeCollapse rangeId="range3" min={pairRef?.min} max={pairRef?.max ?? 10000} />
            </div>
            <div className="casino-bl-box">
              {PLAYERS.map((n) => backCell(findItem(`Pair Plus ${n}`), n, n))}
            </div>
          </div>

          <div className="casino-box-row mb-4">
            <div className="casino-nation-name">
              <b>Total</b>
              {' '}
              <RangeCollapse rangeId="range2" min={totalRef?.min} max={totalRef?.max ?? 10000} />
            </div>
            <div className="casino-bl-box">
              {PLAYERS.map((n) => backCell(findItem(`Total ${n}`), null, n))}
            </div>
          </div>
        </div>
      </div>

      {/* ── MOBILE (d-none-big) ── */}
      <div className="casino-box mb-2 d-none-big">
        <div className="casino-box-header">
          <div className="casino-nation-name no-border"></div>
          <div className="casino-bl-box text-right">
            <div className="casino-bl-box-item">
              <span>Odds</span>
              <RangeCollapse rangeId="range4" min={oddsRef?.min} max={oddsRef?.max ?? 100000} />
            </div>
            <div className="casino-bl-box-item">
              <span>Pair Plus</span>
              {' '}
              <RangeCollapse rangeId="range3" min={pairRef?.min} max={pairRef?.max ?? 10000} />
            </div>
            <div className="casino-bl-box-item">
              <span>Total</span>
              {' '}
              <RangeCollapse rangeId="range6" min={totalRef?.min} max={totalRef?.max ?? 10000} />
            </div>
          </div>
        </div>
        <div className="casino-box-content">
          {PLAYERS.map((n) => (
            <div key={n} className="casino-box-row">
              <div className="casino-nation-name">
                <b>Player {n}</b>
                {' '}
                <div className="">
                  {/* reference: a space between the card spans */}
                  {getPlayerCards(codes, n).map((c, i) => (
                    <Fragment key={i}>
                      {i > 0 && ' '}
                      <span data-v-b64efdfa="">
                        <img data-v-b64efdfa="" src={cardSrc(c)} />
                      </span>
                    </Fragment>
                  ))}
                </div>
              </div>
              {' '}
              <div className="casino-bl-box">
                {mBackCell(findItem(`Player ${n}`), null, `p${n}`)}
                {' '}
                {mBackCell(findItem(`Pair Plus ${n}`), n, `pp${n}`)}
                {' '}
                {mBackCell(findItem(`Total ${n}`), null, `t${n}`)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
});

export default BetTableTeenpattiOpen;
